import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import DashboardLayout from '../components/DashboardLayout';
import { OasisSignModal } from '../components/OasisSignModal';
import { TP_LABELS } from '../utils/oasisPdf';

const Row = ({ a, checked, onToggle }) => {
  const ready = a.missingCount === 0;
  return (
    <tr className={ready ? 'hover:bg-amber-50/40' : 'opacity-50'} data-testid={`sign-row-${a.id}`}>
      <td className="p-3"><input type="checkbox" disabled={!ready} checked={checked} onChange={() => onToggle(a.id)} data-testid={`sign-check-${a.id}`} /></td>
      <td className="p-3 font-medium">{a.patientName}</td>
      <td className="p-3">{a.timepoint} - {TP_LABELS[a.timepoint]}</td>
      <td className="p-3">{a.createdByName || '-'}{a.mine ? ' (you)' : ''}</td>
      <td className="p-3">{new Date(a.updatedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}{a.appointmentId ? <span className="block text-xs text-gray-500">Recert visit</span> : null}</td>
      <td className="p-3">{ready ? <span className="text-green-700">All answered</span> : <span className="text-amber-700">{a.missingCount} unanswered</span>}</td>
      <td className="p-3"><Link to={`/dashboard/forms/oasis-e2?id=${a.id}`} className="text-sm text-blue-700 hover:underline" data-testid={`sign-open-${a.id}`}>Open</Link></td>
    </tr>
  );
};

export default function OasisSignQueuePage() {
  const [rows, setRows] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [picked, setPicked] = useState([]);
  const [modal, setModal] = useState(false);
  const [signing, setSigning] = useState(false);
  const load = () => api.get('/oasis-e2/sign-queue', { params: { include_incomplete: showAll } }).then(r => setRows(r.data.assessments || [])).catch(() => setRows([]));
  useEffect(() => { setPicked([]); load(); }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = (rows || []).filter(a => a.missingCount === 0);
  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const allOn = ready.length > 0 && ready.every(a => picked.includes(a.id));

  const sign = async (body) => {
    setSigning(true);
    try {
      const r = await api.post('/oasis-e2/sign-batch', { ...body, ids: picked });
      const skipped = r.data.results.filter(x => !x.signed);
      toast.success(`Signed and locked ${r.data.signed} assessment(s)${skipped.length ? `; skipped ${skipped.length}: ${skipped.map(x => `${x.patientName || x.id.slice(0, 6)} (${x.reason})`).join(', ')}` : ''}`, { duration: 7000 });
      setModal(false); setPicked([]); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign');
    } finally {
      setSigning(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-4" data-testid="oasis-sign-queue-page">
        <div className="bg-gray-900 text-white rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#d4af37]">Sign OASIS Assessments</h1>
            <p className="text-xs text-white/70">Tick finished assessments and sign them all at once. Your one signature and password lock each one under your name.</p>
          </div>
          <button onClick={() => setModal(true)} disabled={!picked.length} className="px-4 py-2 rounded bg-[#d4af37] text-gray-900 font-semibold text-sm flex items-center gap-2 disabled:opacity-50" data-testid="sign-selected-btn">
            <PenLine className="w-4 h-4" /> Sign selected ({picked.length})
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="sign-show-all" /> Also show assessments with unanswered items</label>
          <span className="text-gray-500" data-testid="sign-ready-count">{ready.length} ready to sign</span>
        </div>
        <div className="bg-white border rounded-lg overflow-x-auto">
          {rows === null ? <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div> : rows.length === 0
            ? <p className="p-6 text-sm text-gray-500" data-testid="sign-queue-empty">Nothing waiting for a signature.</p>
            : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3"><input type="checkbox" checked={allOn} disabled={!ready.length} onChange={() => setPicked(allOn ? [] : ready.map(a => a.id))} data-testid="sign-check-all" /></th>
                    <th className="p-3">Patient</th><th className="p-3">Time point</th><th className="p-3">Created by</th><th className="p-3">Updated</th><th className="p-3">Items</th><th className="p-3" />
                  </tr>
                </thead>
                <tbody className="divide-y">{rows.map(a => <Row key={a.id} a={a} checked={picked.includes(a.id)} onToggle={toggle} />)}</tbody>
              </table>
            )}
        </div>
        {modal && <OasisSignModal onSign={sign} onClose={() => setModal(false)} signing={signing} />}
      </div>
    </DashboardLayout>
  );
}
